<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useContactRemovalConfirmation } from 'dashboard/composables/useContactRemovalConfirmation';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const innerDialogRef = ref(null);

const {
  isLoadingPreview,
  resolvedConversations,
  openConversations,
  purgedConversations,
  requiresCode,
  expectedCode,
  typedCode,
  previewFailed,
  canConfirm,
  confirmationCode,
  loadPreview,
  reset,
} = useContactRemovalConfirmation();

const contactId = computed(
  () => route.params.contactId || props.selectedContact?.id
);

const deleteContact = async () => {
  const id = contactId.value;
  if (!id) return;

  try {
    await store.dispatch('contacts/delete', {
      id,
      confirmationCode: confirmationCode.value,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.message ||
        t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.API.ERROR_MESSAGE')
    );
  }
};

const handleDialogConfirm = async () => {
  // Guarda de verdade, e não só o botão desabilitado: o Dialog confirma no
  // submit do <form>, então um Enter no campo do código chegaria aqui mesmo
  // com o botão cinza.
  if (!canConfirm.value) return;
  emit('goToContactsList');
  await deleteContact();
  innerDialogRef.value?.close();
};

// Os pais abrem o diálogo por `ref.dialogRef.open()`. Mantemos essa forma e
// aproveitamos para pedir a prévia: as contagens têm de ser as de agora, não as
// de quando o componente montou.
const dialogRef = {
  open: () => {
    reset();
    innerDialogRef.value?.open();
    loadPreview(contactId.value);
  },
  close: () => innerDialogRef.value?.close(),
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="innerDialogRef"
    type="alert"
    :title="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.TITLE')"
    :description="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.DESCRIPTION')"
    :confirm-button-label="t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.CONFIRM')"
    :disable-confirm-button="!canConfirm"
    @confirm="handleDialogConfirm"
  >
    <div class="flex flex-col gap-3 text-sm">
      <p v-if="isLoadingPreview" class="mb-0 text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.LOADING_PREVIEW') }}
      </p>

      <p v-else-if="previewFailed" class="mb-0 text-n-ruby-11">
        {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.PREVIEW_FAILED') }}
      </p>

      <template v-else>
        <p v-if="resolvedConversations > 0" class="mb-0 text-n-slate-11">
          {{
            t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.RESOLVED_PRESERVED', {
              count: resolvedConversations,
            })
          }}
        </p>

        <p v-if="openConversations > 0" class="mb-0 text-n-slate-11">
          {{
            t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.OPEN_PRESERVED', {
              count: openConversations,
            })
          }}
        </p>

        <p
          v-if="purgedConversations > 0"
          class="mb-0 font-medium text-n-ruby-11"
        >
          {{
            t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.PURGED_DESTROYED', {
              count: purgedConversations,
            })
          }}
        </p>

        <div v-if="requiresCode" class="flex flex-col gap-1.5">
          <label for="contact-removal-code" class="text-sm text-n-slate-12">
            {{
              t('CONTACTS_LAYOUT.DETAILS.DELETE_DIALOG.CODE_LABEL', {
                code: expectedCode,
              })
            }}
          </label>
          <input
            id="contact-removal-code"
            v-model="typedCode"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            maxlength="4"
            class="w-24 px-3 py-2 text-base tracking-[0.4em] text-center border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12"
          />
        </div>
      </template>
    </div>
  </Dialog>
</template>
